import type { Field } from '#admin/features/cms/api.ts'

export type FieldRow = readonly [Field] | readonly [Field, Field]

const SHORT_KINDS: ReadonlySet<Field['kind']> = new Set([
  'line',
  'url',
  'email',
  'number',
  'date',
  'select',
])

const COMPANION_SUFFIXES: readonly (readonly [string, string])[] = [
  ['label', 'href'],
  ['label', 'url'],
  ['startsat', 'endsat'],
  ['latitude', 'longitude'],
  ['email', 'phone'],
]

export function areCompanions(first: Field, second: Field): boolean {
  if (first.wide || second.wide) return false
  if (!SHORT_KINDS.has(first.kind) || !SHORT_KINDS.has(second.kind)) return false

  const a = first.name.toLowerCase()
  const b = second.name.toLowerCase()
  return COMPANION_SUFFIXES.some(
    ([left, right]) =>
      a.endsWith(left) &&
      b.endsWith(right) &&
      a.slice(0, a.length - left.length) === b.slice(0, b.length - right.length),
  )
}

export function fieldRows(fields: readonly Field[]): FieldRow[] {
  const rows: FieldRow[] = []
  for (let index = 0; index < fields.length; index += 1) {
    const field = fields[index]
    if (field === undefined) continue
    const next = fields[index + 1]
    if (next !== undefined && areCompanions(field, next)) {
      rows.push([field, next])
      index += 1
    } else {
      rows.push([field])
    }
  }
  return rows
}

export type RowSpan = 'cell' | 'pair' | 'full'

export const FIELD_GRID_CLASS = 'grid gap-x-4 gap-y-3 @2xl/fields:grid-cols-2'

export const ROW_SPAN_CLASS: Record<RowSpan, string> = {
  cell: '',
  pair: 'grid gap-x-4 gap-y-3 @lg/fields:grid-cols-2 @2xl/fields:col-span-2',
  full: 'col-span-full',
}

const SHORT_LINE_MAX = 200

const CELL_KINDS: ReadonlySet<Field['kind']> = new Set([
  'url',
  'email',
  'number',
  'date',
  'select',
  'boolean',
  'list',
])

export function isShortField(field: Field): boolean {
  if (field.wide) return false
  if (field.kind === 'line') return field.maxLength !== null && field.maxLength <= SHORT_LINE_MAX
  return CELL_KINDS.has(field.kind)
}

export function rowSpan(row: FieldRow): RowSpan {
  if (row.length === 2) return 'pair'
  return isShortField(row[0]) ? 'cell' : 'full'
}
